import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Building2, Loader2 } from "lucide-react";
import { type FormEvent, useState } from "react";

import { BackLink } from "../components/BackLink";

import { ApiError, accountsApi, usersApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/accounts/new")({
  component: NewAccountPage,
});

function NewAccountPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [ownerId, setOwnerId] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPrivileged = user?.role === "admin" || user?.role === "manager";

  // Manager+ get a user picker for owner assignment. Sales never sees it
  // (they can only create private accounts owned by themselves).
  const { data: users } = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isPrivileged,
  });

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await accountsApi.create({
        name,
        is_public: isPrivileged ? isPublic : false,
        owner_id: isPrivileged ? ownerId.trim() || null : null,
        address: address.trim() || null,
      });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      navigate({ to: "/accounts/$accountId", params: { accountId: created.id } });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not reach the server.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/accounts" fallbackLabel="accounts" />
        <h1 className="text-2xl font-semibold tracking-tight">New account</h1>
        <p className="text-sm opacity-60">
          {isPrivileged
            ? "Choose who owns it and whether it's visible to everyone or only to people you grant."
            : "Sales accounts are private to you. A manager can change the owner or make it public later."}
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-xl border border-[var(--border)] p-5"
        noValidate
      >
        <Field id="account-name" label="Name">
          <input
            id="account-name"
            type="text"
            required
            autoComplete="organization"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Manufacturing Pvt Ltd"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field id="account-address" label="Address" hint="Optional. Default delivery address.">
          <textarea
            id="account-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Shipping / delivery address"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        {isPrivileged && (
          <>
            <Field
              id="account-owner"
              label="Owner"
              hint="Soft label only — owner gets no special access. Use grants on the detail page for per-user access."
            >
              <select
                id="account-owner"
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">(no owner)</option>
                {users?.items.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} — {u.role}
                  </option>
                ))}
              </select>
            </Field>

            <label
              htmlFor="account-public"
              className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--border)] p-3 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              <input
                id="account-public"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="size-4"
              />
              <span>
                <span className="font-medium">Public</span>
                <span className="block text-xs opacity-60">
                  Visible to every Sales user without a grant. Use sparingly.
                </span>
              </span>
            </label>
          </>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            <>
              <Building2 className="size-4" aria-hidden />
              Create account
            </>
          )}
        </button>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80">
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}
